import React, { BaseSyntheticEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  createTheme,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ThemeProvider,
  Toolbar,
} from "@mui/material";
import ActionToast from "../../../shared/components/ActionToast";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import { enumToArray, FileModel, NotificationMessage } from "../../../shared/models/models";
import {
  ChangeStockItemModel,
  CustomerInventoryState,
  productAvailability,
  ProductAvailabilityCode
} from "../models/models";
import {
  changeStockItem,
  createStockItem,
  getCategories,
  getManufacturers,
  getStockItem,
  setError,
  setSuccess
} from "../customerInventorySlice";
import { CurrencyCode, productCondition, ProductConditionCode } from "../../../common/models/CommonModels";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { integerRegex, threeDecimalsRegex, twoDecimalsRegex } from "../../../shared/validators";
import UploadFile from "../../../shared/components/UploadFile";
import QuantityInput from "../../../shared/components/inputs/QuantityInput";
import useTranslation from "../../../shared/hooks/useTranslation";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";

const EditStockItem = () => {
  const { stockItem, manufacturers, categories, loading, success, error } =
    useAppSelector<CustomerInventoryState>((state: any) => state.customerInventory);
  const dispatch = useAppDispatch();
  const { canWrite } = usePermission(Modules.Inventory);
  const isReadOnly = !canWrite;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors },
  } = useForm<ChangeStockItemModel>({
    defaultValues: {
      conditionCode: ProductConditionCode.new
    }
  });

  const navigate = useNavigate();

  const { id, groupId } = useParams();
  const isNew = !id;

  if (!id && !groupId) {
    navigate('/supplier/inventory')
  }

  const [isConsumable, setIsConsumable] = useState(false)
  const [file, setFile] = useState<FileModel | null>(stockItem?.file || null);

  const condition = watch('conditionCode');
  const editCondition = stockItem?.conditionCode !== ProductConditionCode.notApplicable ? stockItem?.conditionCode : ProductConditionCode.new;

  const handlerCreateFile = (file: FileModel | null) => {
    setFile(file);
  };

  const onSubmit: SubmitHandler<ChangeStockItemModel> = (data: ChangeStockItemModel) => {
    data.manufacturerName = manufacturers.find(x => x.id === data.manufacturerId)?.name || "";
    data.alternativeNumber = data.alternativeNumber || undefined;
    data.conditionCode = isConsumable ? Number(ProductConditionCode.notApplicable) : Number(data.conditionCode);
    data.availabilityCode = Number(data.availabilityCode);
    data.price = data.price ? { id: CurrencyCode.eur, value: Number(data.price) } : undefined;
    data.quantity = {
      value: data.quantity?.value ? Number(data.quantity.value) : undefined,
      unitCode: Number(data.quantity?.unitCode)
    };
    data.height = data.height ? Number(data.height) : undefined;
    data.width = data.width ? Number(data.width) : undefined;
    data.depth = data.depth ? Number(data.depth) : undefined;
    data.weight = data.weight ? Number(data.weight) : undefined;
    data.file = file || undefined;
    data.groupId = isNew ? groupId!! : stockItem!.groupId;

    handleSubmitStockItem(data);
  };

  const handleSubmitStockItem = async (model: ChangeStockItemModel) => {
    const action = isNew ?
      createStockItem(model) :
      changeStockItem({ id: id, model: model });

    await dispatch(action);
    if (error) {
      return;
    }

    navigate(`/supplier/inventory/${isNew ? groupId!! : stockItem!.groupId}`);
  };

  const handleCancel = () => {
    navigate(`/supplier/inventory/${isNew ? groupId!! : stockItem!.groupId}`);
  };

  const handleConsumable = (value: BaseSyntheticEvent) => {
    setIsConsumable(value.target.textContent === "Consumables")
  }

  const init = async () => {
    dispatch(getManufacturers());
    dispatch(getCategories());

    if (id) {
      setIsConsumable(stockItem?.conditionCode === ProductConditionCode.notApplicable);
      await dispatch(getStockItem(id));
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (stockItem) {
      const model = { ...stockItem } as unknown as ChangeStockItemModel;
      model.price = model.price?.value as any;
      model.number = stockItem.product.number;
      setIsConsumable(stockItem?.conditionCode === ProductConditionCode.notApplicable);
      reset(model);

      setFile(stockItem.file);
    }
  }, [stockItem]);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important",
          },
        },
      },
    },
  });

  const { translate: tInventory } = useTranslation("inventory");
  const { translate: tCommon } = useTranslation("common");

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">{tInventory("productDetails")}</p>
        </div>

        <div className="flex">
        </div>
      </Toolbar>

      <Box className="grid grid-cols-4 gap-5 p-5">
        <Box className="col-span-2">
          <form id="changeStockItemForm" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <InputLabel required>{tCommon("brand")}</InputLabel>
              <FormControl className="w-full">
                <Select
                  key="manufacturerId"
                  defaultValue={stockItem?.product?.manufacturer?.id || ""}
                  onClose={() => setTimeout(() => trigger("manufacturerId"))}
                  {...register("manufacturerId", { required: tCommon("forms.required") })}
                  error={!!errors.manufacturerId}
                >
                  {manufacturers.map((manufacturer) => (
                    <MenuItem key={manufacturer.id} value={manufacturer.id}>
                      {manufacturer.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>{errors.manufacturerId?.message}</FormHelperText>
              </FormControl>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel required>{tInventory("productNumber")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("number", { required: tCommon("forms.required") })}
                  error={!!errors.number}
                  helperText={errors.number?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("alternativeNumber")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("alternativeNumber")}
                  error={!!errors.alternativeNumber}
                  helperText={errors.alternativeNumber?.message}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel>{tCommon("priceEuro")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("price", { pattern: { value: twoDecimalsRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("quantity")}</InputLabel>
                <QuantityInput quantity={stockItem?.quantity} register={register} trigger={trigger} errors={errors} />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel required>{tInventory("category")}</InputLabel>
              <FormControl className="w-full">
                <Select
                  key="categoryId"
                  defaultValue={stockItem?.product?.category?.id || ""}
                  onClose={(value) => {
                    setTimeout(() => trigger("categoryId"))
                    setTimeout(() => handleConsumable(value))
                  }}
                  {...register("categoryId", { required: tCommon("forms.required") })}
                  error={!!errors.categoryId}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>{errors.categoryId?.message}</FormHelperText>
              </FormControl>
            </div>

            {!isConsumable && <div className="mt-4">
              <InputLabel>{tCommon("condition")}</InputLabel>
              <RadioGroup
                row
                defaultValue={!stockItem ? condition : editCondition}>
                {enumToArray<ProductConditionCode>(ProductConditionCode)
                  .filter(x => x !== ProductConditionCode.notApplicable)
                  .map(code => (
                    <FormControlLabel
                      label={tCommon(`productCondition.${productCondition(code)}`)}
                      value={code}
                      control={<Radio sx={{ color: "#ACAEB3" }} {...register("conditionCode")} />}
                      sx={{ marginRight: 5 }} />
                  ))}
              </RadioGroup>
            </div>}

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel required>{tInventory("availability")}</InputLabel>
                <Select
                  className="w-full"
                  defaultValue={stockItem?.availabilityCode || ProductAvailabilityCode.inStock}
                  {...register("availabilityCode")}
                >
                  {enumToArray<ProductAvailabilityCode>(ProductAvailabilityCode).map((code) => (
                    <MenuItem key={code} value={code}>
                      {productAvailability(code, tInventory as (key: string, options?: any) => string)}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div>
                <InputLabel>{tInventory("weightKg")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("weight", { pattern: { value: threeDecimalsRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.weight}
                  helperText={errors.weight?.message}
                />
              </div>
            </div>


            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <InputLabel>{tInventory("heightMm")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("height", { pattern: { value: integerRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.height}
                  helperText={errors.height?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("widthMm")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("width", { pattern: { value: integerRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.width}
                  helperText={errors.width?.message}
                />
              </div>

              <div>
                <InputLabel>{tInventory("depthMm")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("depth", { pattern: { value: integerRegex, message: tCommon("forms.invalidValue") } })}
                  error={!!errors.depth}
                  helperText={errors.depth?.message}
                />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel>{tInventory("productDescription")}</InputLabel>
              <TextField
                className="w-full textarea"
                multiline
                rows={3}
                {...register("description")}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </div>

            <div className="mt-4">
              <InputLabel>{tCommon("image")}</InputLabel>
              <UploadFile file={file} variant="image" onChangeFile={handlerCreateFile} />
            </div>
          </form>
        </Box>

        <Box className="pt-5">
          <Button form="changeStockItemForm" type="submit" variant="contained" className="w-full" disabled={isReadOnly}>
            {tCommon("buttons.save")}
          </Button>

          <Box className="mt-3">
            <Button onClick={handleCancel} variant="outlined" className="w-full mt-3">
              {tCommon("buttons.cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
export default EditStockItem;
